import UnsavedChangesNotice from './ui/UnsavedChangesNotice';
import Modal from './ui/Modal';
import { useConfirmation } from '../contexts/ConfirmationContext';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Article, User, Schedule } from '../types';
import { ScheduleService } from '../services/scheduleService';
import { ArticleService } from '../services/articleService';
import { formatScheduledAtGerman } from '../utils/dateFormatter';
import { createGermanDateTimeFromParts, getGermanDateTime, GERMAN_TIME_ZONE, isWithinScheduleTolerance, parseScheduledAt } from '../utils/dateTimeUtils';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { 
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface RescheduleModalProps {
  article: Article;
  currentUser: User;
  onClose: () => void;
  onSuccess: () => void;
}

interface TimeSlot {
  date: Date;
  formatted: string;
  hasConflict: boolean;
  conflictingArticle?: Article;
}

export const RescheduleModal: React.FC<RescheduleModalProps> = ({
  article,
  currentUser,
  onClose,
  onSuccess
}) => {
  const isEditorOrAdmin = currentUser.roles.includes('administrator') || currentUser.roles.includes('redakteur');
  const [authorSchedule, setAuthorSchedule] = useState<Schedule | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
  const [customDateTime, setCustomDateTime] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const confirm = useConfirmation();
  const close = async () => {
    if (isSaving) return;
    const original = parseScheduledAt(article.scheduledAt);
    if (selectedSlot && selectedSlot.getTime() !== original?.getTime() && !(await confirm({ title: 'Terminänderung verwerfen?', description: 'Der gewählte Termin ist noch nicht gespeichert.', confirmLabel: 'Änderung verwerfen', danger: true }))) return;
    onClose();
  };

  const formatDateForDateTimeLocal = (date: Date): string => {
    const parts = new Intl.DateTimeFormat('de-DE', {
      timeZone: GERMAN_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(date);

    const getPart = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find(part => part.type === type)?.value || '00';

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}`;
  };

  const parseGermanDateTimeLocal = (value: string): Date | null => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) return null;

    const [, year, month, day, hours, minutes] = match;
    return createGermanDateTimeFromParts({
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hours: Number(hours),
      minutes: Number(minutes)
    });
  };

  useEffect(() => {
    const loadScheduleAndSlots = async () => {
      setIsLoading(true);
      try {
        // WICHTIG: Lade Schedule des ARTIKEL-AUTORS, nicht des eingeloggten Users!
        const authorSchedules = await ScheduleService.getSchedulesByAuthor(article.authorId);
        const platformSchedule = authorSchedules.find(
          s => s.platformId === article.platformId && s.isActive
        );
        
        if (!platformSchedule) {
          if (!isEditorOrAdmin) {
            toast.error('Kein aktiver Termin für diese Plattform gefunden');
            onClose();
            return;
          }
          // Redaktion/Admin darf unabhängig vom festen Wochenslot ein beliebiges Datum setzen.
          setAuthorSchedule(null);
          setAvailableSlots([]);
        } else {
          setAuthorSchedule(platformSchedule);
          
          // Berechne die nächsten 6 verfügbaren Termine
          const slots = await calculateNextSlots(platformSchedule, 6);
          
          // Prüfe Konflikte für jeden Slot
          const slotsWithConflicts: TimeSlot[] = await Promise.all(
            slots.map(async (date) => {
              // Finde konfliktierenden Artikel (außer dem aktuellen)
              const conflictingArticle = await ArticleService.findConflictingArticle(
                article.authorId,
                article.platformId,
                date,
                currentUser,
                article.id // Aktuellen Artikel ausschließen
              );
              
              return {
                date,
                formatted: formatScheduledAtGerman(date.toISOString()),
                hasConflict: conflictingArticle !== null,
                conflictingArticle: conflictingArticle || undefined
              };
            })
          );
          
          setAvailableSlots(slotsWithConflicts);
        }
        
        // Aktuellen Termin als vorausgewählt setzen
        if (article.scheduledAt) {
          const currentDate = parseScheduledAt(article.scheduledAt);
          if (currentDate) {
            setSelectedSlot(currentDate);
            if (isEditorOrAdmin) {
              setCustomDateTime(formatDateForDateTimeLocal(currentDate));
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der Termine:', error);
        toast.error('Fehler beim Laden der verfügbaren Termine');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadScheduleAndSlots();
  }, [article, currentUser, onClose, isEditorOrAdmin]);

  // Berechne die nächsten N Termine basierend auf dem Schedule
  const calculateNextSlots = async (schedule: Schedule, count: number): Promise<Date[]> => {
    const slots: Date[] = [];
    
    // Aktuelle Zeit in deutscher Zeitzone
    const germanNow = getGermanDateTime();
    
    // Konvertiere Schedule dayOfWeek zu JavaScript Format
    let targetDayOfWeekJS;
    if (schedule.dayOfWeek === 6) { // Sonntag in unserem System
      targetDayOfWeekJS = 0; // Sonntag in JavaScript
    } else {
      targetDayOfWeekJS = schedule.dayOfWeek + 1; // +1 weil wir Montag=0 haben
    }
    
    const [scheduleHours, scheduleMinutes] = schedule.time.split(':').map(Number);
    
    // Finde den nächsten Termin
    let currentDate = new Date(germanNow);
    let foundFirst = false;
    
    for (let i = 0; i < 365 && slots.length < count; i++) {
      const testDate = new Date(currentDate);
      testDate.setDate(currentDate.getDate() + i);
      
      if (testDate.getDay() === targetDayOfWeekJS) {
        // Erstelle korrektes Datum mit deutscher Zeitzone
        const germanDateTime = createGermanDateTimeFromParts({
          year: testDate.getFullYear(),
          month: testDate.getMonth() + 1,
          day: testDate.getDate(),
          hours: scheduleHours,
          minutes: scheduleMinutes
        });
        
        // Überspringe vergangene Termine (nur beim ersten Durchlauf)
        if (!foundFirst) {
          if (germanDateTime > germanNow) {
            slots.push(germanDateTime);
            foundFirst = true;
          }
        } else {
          slots.push(germanDateTime);
        }
      }
    }
    
    return slots;
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!selectedSlot) {
      toast.error('Bitte wähle einen Termin aus');
      return;
    }
    
    setIsSaving(true);
    try {
      await ArticleService.updateArticle(article.id, {
        scheduledAt: selectedSlot.toISOString()
      }, currentUser);
      
      toast.success('Artikel erfolgreich umgeplant!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Fehler beim Umplanen:', error);
      toast.error('Fehler beim Umplanen des Artikels');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal title="Artikel umplanen" onClose={close} busy={isSaving}>
          <div className="space-y-4">
            <UnsavedChangesNotice dirty={!!selectedSlot && selectedSlot.getTime() !== parseScheduledAt(article.scheduledAt)?.getTime()} busy={isSaving} />
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Artikel-Info */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Artikel:</p>
                  <p className="text-sm text-gray-900">{article.title || 'Ohne Titel'}</p>
                </div>

                {/* Aktueller Termin */}
                {article.scheduledAt && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-700">Aktueller Termin:</p>
                    <p className="text-sm text-blue-900">{formatScheduledAtGerman(article.scheduledAt)}</p>
                  </div>
                )}

                  {isEditorOrAdmin && (
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Beliebiges Datum/Uhrzeit (nur Redaktion/Admin):
                      </label>
                      <input
                        type="datetime-local"
                        disabled={isSaving}
                        value={customDateTime}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCustomDateTime(value);
                          if (!value) {
                            return;
                          }
                          const parsed = parseGermanDateTimeLocal(value);
                          if (!parsed) {
                            toast.error('Ungültiges Datum/Uhrzeit-Format');
                            return;
                          }
                          setSelectedSlot(parsed);
                        }}
                        className="input-field"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Der gewählte Zeitpunkt wird als Veröffentlichungstermin gespeichert.
                      </p>
                    </div>
                  )}

                {/* Verfügbare Termine */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neuen Termin wählen:
                  </label>
                  
                  {authorSchedule && (
                    <p className="text-xs text-gray-500 mb-3">
                      Regelmäßiger Termin: {ScheduleService.getDayName(authorSchedule.dayOfWeek)} um {authorSchedule.time} Uhr
                    </p>
                  )}

                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableSlots.map((slot, index) => {
                      const isCurrentSlot = article.scheduledAt &&
                        isWithinScheduleTolerance(article.scheduledAt, slot.date);
                      
                      return (
                        <div
                          key={index}
                          className={`relative border rounded-lg p-3 cursor-pointer transition-colors ${
                            selectedSlot?.getTime() === slot.date.getTime()
                              ? 'border-blue-500 bg-blue-50'
                              : slot.hasConflict
                              ? 'border-yellow-300 bg-yellow-50 hover:bg-yellow-100'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => { if (!isSaving) setSelectedSlot(slot.date); }}
                        >
                          <div className="flex items-start">
                            <input
                              type="radio"
                              name="reschedule-slot"
                              aria-label={slot.formatted}
                              disabled={isSaving}
                              checked={selectedSlot?.getTime() === slot.date.getTime()}
                              onChange={() => setSelectedSlot(slot.date)}
                              className="mt-1 mr-3"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {slot.formatted}
                                {isCurrentSlot && (
                                  <span className="ml-2 text-xs text-blue-600">(aktuell)</span>
                                )}
                              </p>
                              
                              {slot.hasConflict && slot.conflictingArticle && (
                                <div className="flex items-start mt-1">
                                  <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mr-1 flex-shrink-0 mt-0.5" />
                                  <p className="text-xs text-yellow-700">
                                    Konflikt mit: "{slot.conflictingArticle.title || 'Ohne Titel'}"
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Hinweis bei Konflikt */}
                {selectedSlot && availableSlots.find(s => s.date.getTime() === selectedSlot.getTime())?.hasConflict && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          Achtung: Terminkollision!
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          Ein anderer Artikel ist bereits für diesen Termin geplant. 
                          Du kannst trotzdem speichern, aber es werden dann zwei Artikel am gleichen Tag veröffentlicht.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
            <Button
              onClick={handleSave}
              disabled={isLoading || isSaving || !selectedSlot}
              className="w-full sm:w-auto"
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Wird gespeichert...</span>
                </>
              ) : (
                'Termin speichern'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={close}
              disabled={isSaving}
              className="w-full sm:w-auto mt-3 sm:mt-0"
            >
              Abbrechen
            </Button>
          </div>
    </Modal>
  );
};

